import { LoanConfigurationDto } from "api/borrower";
import { GuaranteeDto } from "api/pricing";
import { ReferentialKey } from "api/referential";
import { Button } from "components/ui/Button";
import { UmbrellaIcon } from "components/icons/customIcons";
import { LinkIcon, PenIcon } from "components/icons/Icons";
import { NsKey } from "enums/NsKey";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { FC, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { GuaranteeButton } from "./GuaranteeButton";
import { PsyBuybackToggle } from "./PsyBuybackToggle";
import { BorrowerGuaranteeForm } from "../guarantees-card";
import { Sizes } from "enums/sizes";
import { usePanelWithBorrowerPricingContext } from "../../hooks/usePanelWithBorrowerPricingContext";

interface PricingLoanCardProps {
  config: LoanConfigurationDto;
  index: number;
  guaranteesReferential: GuaranteeDto[];
  currentGuaranteeIds: string[];
  isPsyBuyback: boolean;
  onToggleLoan: (borrowerLoanId: string) => void;
  onToggleGuarantee: (loanId: string, guarantee: GuaranteeDto) => void;
  onTogglePsyBuyback: (loanId: string) => void;
}

// Ordre de tri pour les garanties optionnelles
const OPTIONAL_GUARANTEE_ORDER: Record<string, number> = {
  "ITT": 1,
  "IPT": 2,
  "IPP": 3
};

export const PricingLoanCard: FC<PricingLoanCardProps> = ({
  config,
  index,
  guaranteesReferential,
  currentGuaranteeIds,
  isPsyBuyback,
  onToggleLoan,
  onToggleGuarantee,
  onTogglePsyBuyback
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower]);
  const { openPanel } = usePanelWithBorrowerPricingContext();
  const loanTypeReferential = useSelector(selectReferentialDataByKey(ReferentialKey.LoanType));

  const isDisabled = !config.insuranceConfig.isActive;
  const { loanTypeId, loanAmount } = config.loan;

  const loanTypeLabel = useMemo(() => {
    const loanTypeRef = loanTypeReferential.find(item => item.id === loanTypeId);
    return loanTypeRef ? loanTypeRef.label.toLowerCase() : '';
  }, [loanTypeReferential, loanTypeId]);

  const sortedGuarantees = useMemo(() => {
    return [...guaranteesReferential].sort((a, b) => {
      if (a.isRequired && !b.isRequired) return -1;
      if (!a.isRequired && b.isRequired) return 1;

      if (!a.isRequired && !b.isRequired) {
        const orderA = OPTIONAL_GUARANTEE_ORDER[a.name] || 999;
        const orderB = OPTIONAL_GUARANTEE_ORDER[b.name] || 999;
        return orderA - orderB;
      }

      return 0;
    });
  }, [guaranteesReferential]);

  // Vérifier si ITT est actif pour afficher le toggle Dos Psy
  const isITTActive = useMemo(() => {
    const ittGuarantee = guaranteesReferential.find(g => g.name === "ITT");
    return ittGuarantee ? currentGuaranteeIds.includes(ittGuarantee.id) : false;
  }, [guaranteesReferential, currentGuaranteeIds]);

  const handleSelectLoan = useCallback((e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    openPanel(
      {
        title: `Prêt ${index + 1}`,
        size: Sizes["2xl"],
      },
      <BorrowerGuaranteeForm loanConfiguration={config} />
    );
  }, [isDisabled, index, config, openPanel]);

  const handleToggleLoan = useCallback(() => {
    onToggleLoan(config.insuranceConfig.borrowerLoanId);
  }, [onToggleLoan, config.insuranceConfig.borrowerLoanId]);

  const handleToggleGuarantee = useCallback((guarantee: GuaranteeDto) => {
    onToggleGuarantee(config.loan.id, guarantee);
  }, [onToggleGuarantee, config.loan.id]);

  const handleTogglePsyBuyback = useCallback(() => {
    onTogglePsyBuyback(config.loan.id);
  }, [onTogglePsyBuyback, config.loan.id]);

  const containerClassName = isDisabled
    ? "flex w-full p-4 border-r gap-2 opacity-50 cursor-not-allowed"
    : "flex w-full p-4 border-r gap-2";

  return (
    <div className={containerClassName}>
      <div className="flex-none h-8 w-8 flex justify-center items-center bg-secondary rounded-full">
        <UmbrellaIcon className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex flex-col w-full gap-2">
        <p className="text-sm font-light">
          Prêt {index + 1} : {config.loan.totalDuration} mois
        </p>
        <p className="text-sm">
          Prêt {loanTypeLabel} de {loanAmount} €
        </p>
        <div className="flex gap-1 overflow-y-auto flex-wrap items-center">
          {sortedGuarantees.map((guarantee) => (
            <GuaranteeButton
              key={guarantee.id}
              guarantee={guarantee}
              currentGuaranteeIds={currentGuaranteeIds}
              guaranteesReferential={guaranteesReferential}
              onToggle={handleToggleGuarantee}
            />
          ))}
          {isITTActive && (
            <>
              <div className="w-px h-3 bg-gray-300 mx-1" />
              <PsyBuybackToggle
                isPsyBuyback={isPsyBuyback}
                onToggle={handleTogglePsyBuyback}
              />
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col justify-between">
        <Button
          color="slate"
          tooltip={t('BORROWER_LOAN_FORM.TOOLTIPS.UPDATE_LOAN_GUARANTEES')}
          round
          className="self-baseline"
          onClick={handleSelectLoan}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        >
          <PenIcon className="text-black" />
        </Button>
        <Button
          tooltip={t('BORROWER_LOAN_FORM.TOOLTIPS.LINK_LOAN_TO_BORROWER')}
          color="slate"
          round
          className="self-baseline"
          onClick={handleToggleLoan}
        >
          <LinkIcon className="text-black" />
        </Button>
      </div>
    </div>
  );
};
